import React, { useMemo } from 'react';
import {
  CLOUD_PROVIDER_OPTIONS,
  HARDWARE_DEVICE_OPTIONS,
  getAcceleratorOptions,
  getInstanceTypeOptions,
} from '@/features/evaluation/api/performanceMetricsApi';
import { LabeledDropdown, SectionTitle } from './fields';

function firstOrNull(arr) {
  return Array.isArray(arr) && arr.length ? arr[0] : null;
}

export function pickFirstInstance(catalog, cloud, hardware, accelerator) {
  return firstOrNull(getInstanceTypeOptions(cloud, hardware, accelerator, catalog));
}

/** First cloud → hardware → accelerator → instance of the catalog (modal default). */
export function pickFirstSelection(catalog) {
  const cloud = firstOrNull(Object.keys(catalog || {}));
  if (!cloud) return null;
  const hardware = firstOrNull(Object.keys(catalog[cloud] || {}));
  if (!hardware) return null;
  const accelerators = getAcceleratorOptions(cloud, hardware, catalog);
  const accelerator = accelerators[0]?.value ?? null;
  const instance = pickFirstInstance(catalog, cloud, hardware, accelerator);
  return { cloud, hardware, accelerator, instance };
}

export function findInstanceMeta(catalog, cloud, hardware, accelerator, instanceType) {
  const opts = getInstanceTypeOptions(cloud, hardware, accelerator, catalog);
  return opts.find((o) => o.value === instanceType) || null;
}

function withKnownLabels(keys, knownOptions) {
  return keys.map((value) => knownOptions.find((o) => o.value === value) || { value, label: value });
}

/**
 * Figma 9952:24284 "Infrastructure": Cloud Provider | Hardware Device |
 * Accelerator | Instance type in one row. Changing a level resets the
 * levels below it to the first catalog entry.
 */
export default function InfrastructureSection({
  catalog,
  cloudProvider,
  hardwareDevice,
  accelerator,
  instanceType,
  onChange,
}) {
  const cloudOptions = useMemo(() => {
    const keys = Object.keys(catalog || {});
    return keys.length ? withKnownLabels(keys, CLOUD_PROVIDER_OPTIONS) : CLOUD_PROVIDER_OPTIONS;
  }, [catalog]);

  const hardwareOptions = useMemo(() => {
    const keys = Object.keys(catalog?.[cloudProvider] || {});
    return keys.length ? withKnownLabels(keys, HARDWARE_DEVICE_OPTIONS) : HARDWARE_DEVICE_OPTIONS;
  }, [catalog, cloudProvider]);

  const acceleratorOptions = useMemo(
    () => getAcceleratorOptions(cloudProvider, hardwareDevice, catalog),
    [catalog, cloudProvider, hardwareDevice]
  );

  const instanceOptions = useMemo(
    () => getInstanceTypeOptions(cloudProvider, hardwareDevice, accelerator, catalog),
    [catalog, cloudProvider, hardwareDevice, accelerator]
  );

  const emit = (cloud, hardware, acc, instanceOpt) =>
    onChange({
      cloudProvider: cloud,
      hardwareDevice: hardware,
      accelerator: acc,
      instanceType: instanceOpt?.value || null,
      instanceMeta: instanceOpt,
    });

  const handleCloud = (value) => {
    const hardware = firstOrNull(Object.keys(catalog?.[value] || {}));
    const nextAcc = getAcceleratorOptions(value, hardware, catalog)[0]?.value ?? null;
    emit(value, hardware, nextAcc, pickFirstInstance(catalog, value, hardware, nextAcc));
  };

  const handleHardware = (value) => {
    const nextAcc = getAcceleratorOptions(cloudProvider, value, catalog)[0]?.value ?? null;
    emit(cloudProvider, value, nextAcc, pickFirstInstance(catalog, cloudProvider, value, nextAcc));
  };

  const handleAccelerator = (value) => {
    emit(cloudProvider, hardwareDevice, value, pickFirstInstance(catalog, cloudProvider, hardwareDevice, value));
  };

  const handleInstance = (value) => {
    emit(
      cloudProvider,
      hardwareDevice,
      accelerator,
      findInstanceMeta(catalog, cloudProvider, hardwareDevice, accelerator, value)
    );
  };

  return (
    <section className="configure-deployment-modal__section" aria-label="Infrastructure">
      <SectionTitle>Infrastructure</SectionTitle>
      <div className="configure-deployment-modal__fields-row">
        <LabeledDropdown
          label="Cloud Provider"
          tooltip="Cloud where the model will run"
          options={cloudOptions}
          value={cloudProvider}
          onChange={handleCloud}
        />
        <LabeledDropdown
          label="Hardware Device"
          tooltip="CPU or GPU hardware"
          options={hardwareOptions}
          value={hardwareDevice}
          onChange={handleHardware}
        />
        <LabeledDropdown
          label="Accelerator"
          tooltip="GPU accelerator type, if applicable"
          options={acceleratorOptions}
          value={accelerator ?? null}
          onChange={handleAccelerator}
          placeholder={acceleratorOptions.length ? 'Select' : '(none)'}
          disabled={!acceleratorOptions.length}
        />
        <LabeledDropdown
          label="Instance type"
          tooltip="Machine instance type"
          options={instanceOptions}
          value={instanceType}
          onChange={handleInstance}
          disabled={!instanceOptions.length}
        />
      </div>
    </section>
  );
}
